import { type I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Group,
  MultiSelect,
  NumberInput,
  Paper,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useDebouncedCallback } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useBlocker } from "@tanstack/react-router";
import { z } from "zod";

import {
  getGetBookingQueryKey,
  getGetBookingsQueryKey,
  useUpdateBookingGroupInformation,
} from "@/shared/api/generated/booking";
import {
  GetBookingData,
  GetBookingResponse,
  GetBookingsResponse,
} from "@/shared/api/generated/schemas";
import {
  GetFieldsBySubjectKind,
  getSubject,
} from "@/shared/providers/casl-provider/authorization/subjects";
import { useAppAbility } from "@/shared/providers/casl-provider/use-app-ability";
import { countryListnbNO } from "@/shared/utils/countryList";
import { langugeListnbNO } from "@/shared/utils/languageList";

function useGroupInformationFormSchema() {
  const { t } = useLingui();

  return z.object({
    groupName: z.string(),
    contactPersonName: z.string(),
    contactPersonPhone: z.string(),
    nationalities: z.array(
      z.string().min(1, { message: t`Velg nasjonaliteter` }),
    ),
    languages: z.array(z.string().min(1, { message: t`Velge språk` })),
    guestsBooked: z.number().min(0, { message: t`Velg antall` }),
    staffBooked: z.number().min(0, { message: t`Velg antall` }),
    adultsArrived: z.number().min(0, { message: t`Velg antall` }),
    childrenPaidArrived: z.number().min(0, { message: t`Velg antall` }),
    childrenFreeArrived: z.number().min(0, { message: t`Velg antall` }),
    staffArrived: z.number().min(0, { message: t`Velg antall` }),
  });
}

type GroupInformationFormInitial = z.input<
  ReturnType<typeof useGroupInformationFormSchema>
>;
type GroupInformationFormSubmit = z.output<
  ReturnType<typeof useGroupInformationFormSchema>
>;

const favouriteLanguages = ["no", "en", "de", "fr", "es", "it", "pl", "nl"];

const groupedLanguageList = (i18n: I18n) => [
  {
    group: i18n.t(msg`Ofte brukt`),
    items: langugeListnbNO.filter((lang) =>
      favouriteLanguages.includes(lang.value),
    ),
  },
  {
    group: i18n.t(msg`Annen`),
    // Filter out languages that are already in favorites
    items: langugeListnbNO.filter(
      (lang) => !favouriteLanguages.includes(lang.value),
    ),
  },
];

const favouriteCountries = [
  "NO",
  "SE",
  "DK",
  "FI",
  "IS",
  "DE",
  "GB",
  "FR",
  "ES",
  "IT",
  "PL",
  "NL",
];

const groupedCountryList = (i18n: I18n) => [
  {
    group: i18n.t(msg`Ofte brukt`),
    items: countryListnbNO.filter((country) =>
      favouriteCountries.includes(country.value),
    ),
  },
  {
    group: i18n.t(msg`Annen`),
    items: countryListnbNO.filter(
      (country) => !favouriteCountries.includes(country.value),
    ),
  },
];

export default function GroupInformation({
  booking,
}: {
  booking: GetBookingData;
}) {
  const { i18n, t } = useLingui();

  const debouncedSubmit = useDebouncedCallback(() => {
    form.onSubmit(handleSubmit)();
  }, 1000);

  const groupInformationFormSchema = useGroupInformationFormSchema();

  const form = useForm<
    GroupInformationFormInitial,
    () => GroupInformationFormSubmit
  >({
    validate: zodResolver(groupInformationFormSchema),
    initialValues: {
      groupName: booking.group?.name ?? "",
      contactPersonName: booking.group?.contactPerson ?? "",
      contactPersonPhone: booking.group?.phone ?? "",
      nationalities: booking.group?.nationalities ?? [],
      languages: booking.group?.languages ?? [],
      guestsBooked: booking.group?.guests.booked ?? 0,
      staffBooked: booking.group?.travelLeader.booked ?? 0,
      adultsArrived: booking.group?.arrivals.adults ?? 0,
      childrenPaidArrived: booking.group?.arrivals.childrenPaid ?? 0,
      childrenFreeArrived: booking.group?.arrivals.childrenFree ?? 0,
      staffArrived: booking.group?.arrivals.staff ?? 0,
    },
    onValuesChange: debouncedSubmit,
  });

  useBlocker({
    blockerFn: () =>
      window.confirm(
        t`Det er gjort endringer i skjemaet som ikke er lagret. Trykk "OK" for å forkaste endringene.`,
      ),
    condition: form.isDirty(),
  });

  const queryClient = useQueryClient();

  const { mutateAsync: updateBookingGroupInformation } =
    useUpdateBookingGroupInformation({
      mutation: {
        onSuccess: (data) => {
          queryClient.setQueryData<GetBookingResponse>(
            getGetBookingQueryKey(booking.id),
            (prev) => ({
              ...prev,
              data: { ...prev?.data, ...data.data },
            }),
          );
          // Update this booking in the for GET /bookings
          queryClient.setQueriesData<GetBookingsResponse>(
            { queryKey: getGetBookingsQueryKey() },
            (old) => ({
              ...old,
              data: (old?.data ?? []).map((booking) =>
                booking.id === data.data.id ? data.data : booking,
              ),
            }),
          );
        },
      },
    });

  async function handleSubmit(values: GroupInformationFormInitial) {
    await updateBookingGroupInformation({
      id: booking.id,
      data: {
        name: values.groupName,
        contactPerson: values.contactPersonName,
        phone: values.contactPersonPhone,
        nationalities: values.nationalities,
        languages: values.languages,
        guestsBooked: values.guestsBooked,
        travelLeadersBooked: values.staffBooked,
        adultsArrived: values.adultsArrived,
        childrenPaidArrived: values.childrenPaidArrived,
        childrenFreeArrived: values.childrenFreeArrived,
        staffArrived: values.staffArrived,
        updatedAt: booking.updatedAt,
      },
    });
    form.resetDirty(values);
  }

  const ability = useAppAbility();

  const canUpdateBooking = (field: GetFieldsBySubjectKind<"Booking">) =>
    ability.can("update", getSubject("Booking", booking), field);

  return (
    <Paper p="xs" h={"100%"}>
      <Text size="sm" mb="sm">
        <Trans>Gruppeinformasjon</Trans>
      </Text>

      <Stack gap="sm">
        <TextInput
          label={t`Gruppe`}
          placeholder={t`Gruppe`}
          {...form.getInputProps("groupName")}
          key={form.key("groupName")}
          readOnly={!canUpdateBooking("group.name")}
        />

        <Group grow align="flex-start">
          <TextInput
            label={t`Kontaktperson`}
            placeholder={t`Kontaktperson`}
            {...form.getInputProps("contactPersonName")}
            key={form.key("contactPersonName")}
            readOnly={!canUpdateBooking("group.contactPerson")}
          />
          <TextInput
            label={t`Mobil`}
            placeholder={t`Mobilnummer`}
            {...form.getInputProps("contactPersonPhone")}
            key={form.key("contactPersonPhone")}
            readOnly={!canUpdateBooking("group.phone")}
          />
        </Group>

        <Group grow align="flex-start">
          <MultiSelect
            label={t`Nasjonalitet`}
            placeholder={t`Nasjonalitet`}
            data={groupedCountryList(i18n)}
            searchable
            clearable
            {...form.getInputProps("nationalities")}
            key={form.key("nationalities")}
            readOnly={!canUpdateBooking("group.nationalities")}
          />
          <MultiSelect
            label={t`Språk`}
            placeholder={t`Språk`}
            data={groupedLanguageList(i18n)}
            searchable
            clearable
            {...form.getInputProps("languages")}
            key={form.key("languages")}
            readOnly={!canUpdateBooking("group.languages")}
          />
        </Group>

        <Group grow align="flex-start">
          <NumberInput
            label={t`Forventet antall`}
            {...form.getInputProps("guestsBooked")}
            key={form.key("guestsBooked")}
            readOnly={!canUpdateBooking("group.guests.booked")}
          />
          <NumberInput
            label={t`Forventet reiseleder/sjåfør`}
            {...form.getInputProps("staffBooked")}
            key={form.key("staffBooked")}
            readOnly={!canUpdateBooking("group.travelLeader.booked")}
          />
        </Group>

        <Stack gap={0}>
          <Text size="sm">
            <Trans>Ankommet antall:</Trans>
          </Text>

          <Group grow align="flex-end">
            <NumberInput
              label={t`Voksne`}
              {...form.getInputProps("adultsArrived")}
              key={form.key("adultsArrived")}
              readOnly={!canUpdateBooking("group.arrivals.adults")}
            />
            <NumberInput
              label={t`Reiseleder/sjåfør`}
              {...form.getInputProps("staffArrived")}
              key={form.key("staffArrived")}
              readOnly={!canUpdateBooking("group.arrivals.staff")}
            />
            <NumberInput
              label={t`Barn (gratis)`}
              {...form.getInputProps("childrenFreeArrived")}
              key={form.key("childrenFreeArrived")}
              readOnly={!canUpdateBooking("group.arrivals.childrenFree")}
            />
            <NumberInput
              label={t`Barn (betalende)`}
              {...form.getInputProps("childrenPaidArrived")}
              key={form.key("childrenPaidArrived")}
              readOnly={!canUpdateBooking("group.arrivals.childrenPaid")}
            />
            <NumberInput
              label={t`Ankommet`}
              value={booking.group?.arrivals.sum ?? 0}
              hideControls
              disabled
              readOnly
            />
          </Group>
        </Stack>
      </Stack>
    </Paper>
  );
}
